'use strict';

import React, {useState, useMemo, useRef, useEffect} from "react";
import moment from "moment";

import config from '../../shared/config';
import {apiCall} from "../../shared/api-client";
import {formatValue, formatNumber, safeNumber, formatDate} from '../../shared/formatters';
import {addSummaries} from '../../shared/summaries';
import {Day0, Month0, Months, LifeIntervals, Weeks, daysRange} from "../../shared/intervals";
import {getIn} from "../../shared/utils";
import {isStale, useStaleDataLoader} from "../../react-components/stale-data-hooks";

const APIEndpoint = 'stats/dashboard/by-filter';
const GroupBy = 'environment';
const GeneratedImagesType = ['2DODFrame', 'Complete'];

const COLUMNS = [
	['This hour', 'hour0'],
	['Last hour', 'hour1'],
	['Last 24 Hours', 'hour24'],
	['Today', 'day0'],
	['Yesterday', 'day1'],
	['This month', 'month00'],
	['Last month', 'month01'],
	['Lifetime', 'life']
];

function IntervalTooltip({interval, caption}) {
	const ref = useRef(null);
	useEffect(() => {
		if (!ref) {
			return;
		}

	}, [ref.current])

	return (
		<div className="interval" ref={ref}>
			<div className="interval-caption">{caption}</div>
			<div className="interval-tooltip">
				From: {formatDate(interval.from)}<br/>
				To: {formatDate(interval.to)}
			</div>
		</div>
	);
}

function DataTD({summary}) {
	if (!summary) {
		return <td data-empty="Interval summary was not in the api response"/>;
	}
	if (summary.stale) {
		return <td className="stale"/>;
	}
	let value = formatValue(summary, 'count', formatNumber);
	if (value === null) {
		return <td data-empty="Value is null"/>;
	}
	return <td>{value}</td>;
}

function SummaryTR({title, data}) {
	// Prepare data
	let {summaries} = data;
	if (!Array.isArray(summaries)) {
		return null;
	}
	let summariesByInterval = summaries.reduce(
		(o, s) => {
			let interval = s.interval.name;
			o[interval] = s;
			return o;
		}, {});


	summariesByInterval.life = LifeIntervals.reduce(
		(life, interval) => addSummaries(life, summariesByInterval[interval]),
		{count: 0, stale: false}
	);

	return <tr>
		<td>{title}</td>
		{
			COLUMNS.map(([t, i]) => <DataTD key={i} title={t} summary={summariesByInterval[i]}/>)
		}
	</tr>
}


function Usage2DTable({data}) {
	if (!data) {
		return <div className="loader">Loading 2d usage data</div>;
	}
	return <section>
		<h2>Generated 2D images summary, by service</h2>
		<table className="metrics-table cm-table table table-stripped usage-2d">
			<thead>
				<tr>
					<th>Service</th>
					{
						COLUMNS.map(
							([t, k]) => (
								<th key={k}>{t}</th>
							)
						)
					}
				</tr>
			</thead>
			<tbody>
				{
					Object.entries(data)
						.map(
							([service, d]) => <SummaryTR key={`mt-row-${service}`} title={service} data={d}/>
						)
				}
			</tbody>
		</table>
	</section>;
}

function ServiceByMonthTR({service, row}) {
	let avg = [];
	let monthTDs = [];
	for (let m of Months) {
		let count = safeNumber(getIn(row, [m, 'count']));
		if (count) {
			avg.push(count);
		}
		monthTDs.push(<td key={m}>{formatNumber(count)}</td>);
	}
	let l = avg.length;
	if (l) {
		avg = avg.reduce((a, i) => a + i, 0) / l;
	} else {
		avg = 0;
	}

	return (
		<tr>
			<td>{service}</td>
			<td>{formatNumber(getIn(row, [Month0, 'count']))}</td>
			<td>{formatNumber(avg)}</td>
			{monthTDs}
		</tr>
	);
}

function Usage2DByMonth({data}) {
	if (!data) {
		return null;
	}
	let services = Object.keys(data);
	let months = Months.reduce(
		(o, m) => {
			o.set(m, null);
			return o;
		},
		new Map()
	);
	let rows = {};
	for (let s of services) {
		let summaries = getIn(data, [s, 'summaries']);
		let row = {};
		rows[s] = row;
		for (let summary of summaries) {
			let intervalName = getIn(summary, ['interval', 'name']);
			if (months.has(intervalName)) {
				row[intervalName] = summary;
				if (months.get(intervalName) === null) {
					months.set(intervalName, summary.interval);
				}
			} else if (intervalName === Month0) {
				row[intervalName] = summary;
			}
		}
	}

	return <section>
		<h2>Generated 2D images, by month</h2>
		<table className="metrics-table cm-table table table-stripped usage-2d-by-month">
			<thead>
				<tr>
					<th>Service</th>
					<th>Current</th>
					<th>Average</th>
					{
						Array.from(months.values()).map(
							interval => (
								<th key={interval.name}>
									<IntervalTooltip
										interval={interval}
										caption={moment(interval.from).utc().format('MMM')}
									/>
								</th>
							)
						)
					}
				</tr>
			</thead>
			<tbody>
				{
					Object.entries(rows).map(
						([service, row]) => <ServiceByMonthTR key={service} service={service} row={row}/>
					)
				}
			</tbody>
		</table>
	</section>;
}


function * getIntervalSummaries(summaries, ...intervals) {
	intervals = new Set(intervals);
	for (let s of summaries) {
		let i = getIn(s, ['interval', 'name']);
		if (intervals.has(i)) {
			yield [i, s];
		}
	}
}

const days = new Set(daysRange(1, 6));
const weeks = new Set(Weeks);

function Usage2DByDay({data}) {
	if (!data) {
		return null;
	}

	let days = new Set(daysRange(1, 6));
	let weeks = new Set(Weeks);
	let columns = [... days, ... weeks];
	let intervals = new Map();

	let rows = [];
	for (let [service, serviceData] of Object.entries(data)) {
		let r = {service};
		let {summaries} = serviceData;
		let intervalSummaries = new Map(getIntervalSummaries(summaries, Day0, ... columns));
		let current = intervalSummaries.get(Day0);
		r.current = (current && current.count) || 0;
		let total = 0;

		for (let col of columns) {
			let s = intervalSummaries.get(col);
			if (s) {
				if (!intervals.has(col)) {
					intervals.set(col, s.interval);
				}
				let c = s.count || 0;
				r[col] = c;
				total += c;
			}
		}

		r.total = total;
		rows.push(r);
	}
	columns = columns.filter(c => intervals.has(c));

	return <section>
		<h2>Generated 2D images, by day</h2>
		<table className="metrics-table cm-table table table-stripped usage-2d-by-day">
			<thead>
				<tr>
					<th>Service</th>
					<th>Current</th>
					<th>Average</th>
					{
						columns.map(
							c => {
								let i = intervals.get(c);
								let {name, from} = i;
								return <th key={name}>
									<IntervalTooltip
										interval={i}
										caption={moment(from).utc().format('DD/MM')}
									/>
								</th>;
							}
						)
					}
				</tr>
			</thead>
			<tbody>
				{
					rows.map(
						r => {
							let {service, current, total} = r;
							let n = columns.length;
							return <tr key={service}>
								<td>{service}</td>
								<td>{formatNumber(current)}</td>
								<td>{formatNumber(Math.floor(total / n))}</td>
								{
									columns.map(c => <td key={c}>{formatNumber(r[c])}</td>)
								}
							</tr>
						}
					)
				}
			</tbody>
		</table>
	</section>;
}

function Dashboard2D() {
	const filter = config.get('CustomerInfo.Usage2DFilter', null);
	const [data, setData] = useState(null);
	const loader = useMemo(() => {
		return async (cancelled) => {
			let response = await apiCall({
				method: APIEndpoint,
				params: Object.assign({type: GeneratedImagesType, groupBy: GroupBy}, filter),
				applyConfiguredFilters: false,
				customiser: function(a, b, key){
					// To sum prelives instead of overwrite them
					if(key === 'summaries'){
						if (b) {
							for (let i = 0; i < a.length; i++) {
								a[i]['count'] += b[i]['count']
								a[i]['total'] += b[i]['total']
							}
						}
						return a;
					}
					else return undefined;
				}
			});
			let data = response.data;
			// TODO: Remove it when API endpoint is implemented properly
			for (let [k, v] of Object.entries(data)) {
				let isEmpty = true;
				let {summaries} = v;
				if (summaries) {
					for (let s of summaries) {
						if (s && s.count) {
							isEmpty = false;
							break;
						}
					}
				}
				if (isEmpty) {
					delete data[k];
				}
			}
			if (!cancelled.current) {
				setData(data);
			}
			return isStale(data);
		};
	}, [setData, filter]);

	useStaleDataLoader(loader);

	return (
		<div id="cm-dashboard2d">
			<h1 className="page-header">2D Usage</h1>
			<section id="dashboard-usage-2d">
				<Usage2DTable data={data}/>
				<Usage2DByMonth data={data}/>
				<Usage2DByDay data = {data}/>
			</section>
		</div>
	);
}

export default Dashboard2D;